/**
 * ActivityDrawer — Left-side slide-out panel for the Discover page showing
 * recent community and project activity.
 *
 * Collapsed: slim vertical strip on left edge with rotated "Activity" label.
 * Expanded: 340px left panel with scrollable list and filter toggles.
 */

import React, { useState, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { usePersistentCollapse } from "../../hooks/usePersistentCollapse";
import { normalizeGuid } from "../../utils/guid";
import { resolveEntityCoords } from "./utils/communityGeoLookup";
import styles from "./ActivityDrawer.module.css";

/* ================================================================
   Types
   ================================================================ */

type ActivityType = "community" | "project";
type FilterMode = "all" | "community" | "project";

interface ActivityEntry {
  id: string;
  type: ActivityType;
  name: string;
  timestamp: Date;
  /** Relative time string e.g. "2 hours ago" */
  displayTime: string;
  /** Community entity type (geo_country, etc.) or project category */
  subtype?: string;
  /** ISO 3166-1 alpha-2 country code for geo communities (e.g. "US", "IN") */
  countryCode?: string;
}

interface Props {
  communities: Record<string, any>[];
  /** Parent-child relationships between community entities. */
  communityRelationships?: Record<string, any>[];
  projects: Record<string, any>[];
  /** Called when user clicks an entry with known coordinates. entityId is the hotspot ID for visual highlighting. */
  onHighlight?: (lat: number, lon: number, entityId?: string) => void;
  /** Notify parent when drawer opens/closes */
  onOpenChange?: (open: boolean) => void;
}

/* ================================================================
   Helpers
   ================================================================ */

function timeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function parseDate(raw: string | undefined | null): Date | null {
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

function prettyType(t: string): string {
  return t
    .replace(/^geo_/, "")
    .replace(/^org_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Convert an ISO 3166-1 alpha-2 country code to its flag emoji.
 * Works by mapping A-Z to Unicode Regional Indicator Symbols.
 */
function countryCodeToFlag(cc: string): string | null {
  const upper = cc.toUpperCase().trim();
  if (upper.length !== 2) return null;
  const a = upper.charCodeAt(0);
  const b = upper.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return null;
  return String.fromCodePoint(a - 65 + 0x1F1E6, b - 65 + 0x1F1E6);
}


/** US state abbreviations → full state names. */
const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
  MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
  OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
  DC: "District of Columbia", PR: "Puerto Rico", GU: "Guam", VI: "Virgin Islands",
  AS: "American Samoa", MP: "Northern Mariana Islands",
};

/** Canadian province abbreviations → full names. */
const CA_PROVINCE_NAMES: Record<string, string> = {
  AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
  NL: "Newfoundland", NS: "Nova Scotia", NT: "Northwest Territories",
  NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island",
  QC: "Quebec", SK: "Saskatchewan", YT: "Yukon",
};

/** Indian state abbreviations → full names. */
const IN_STATE_NAMES: Record<string, string> = {
  AP: "Andhra Pradesh", AR: "Arunachal Pradesh", AS: "Assam", BR: "Bihar",
  CG: "Chhattisgarh", GA: "Goa", GJ: "Gujarat", HR: "Haryana",
  HP: "Himachal Pradesh", JK: "Jammu & Kashmir", JH: "Jharkhand", KA: "Karnataka",
  KL: "Kerala", MP: "Madhya Pradesh", MH: "Maharashtra", MN: "Manipur",
  ML: "Meghalaya", MZ: "Mizoram", NL2: "Nagaland", OD: "Odisha",
  PB: "Punjab", RJ: "Rajasthan", SK: "Sikkim", TN: "Tamil Nadu",
  TS: "Telangana", TR: "Tripura", UK: "Uttarakhand", UP: "Uttar Pradesh",
  WB: "West Bengal", DL: "Delhi",
};

/** Build a guid → entity lookup from the communities array. */
function buildEntityMap(communities: Record<string, any>[]): Map<string, Record<string, any>> {
  const map = new Map<string, Record<string, any>>();
  for (const c of communities) {
    const guid = (c.entity_guid || c.entityGuid || c.id || "").toUpperCase();
    if (guid) map.set(guid, c);
  }
  return map;
}

/** Build a child_guid → parent_guid lookup from relationships. */
function buildParentMap(relationships: Record<string, any>[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of relationships) {
    const child = (r.child_entity_guid || r.childEntityGuid || "").toUpperCase();
    const parent = (r.parent_entity_guid || r.parentEntityGuid || "").toUpperCase();
    if (child && parent) map.set(child, parent);
  }
  return map;
}

/** Walk up the parent tree to find the immediate parent entity. */
function findParent(
  entityGuid: string,
  parentMap: Map<string, string>,
  entityMap: Map<string, Record<string, any>>,
): Record<string, any> | null {
  const parentGuid = parentMap.get(entityGuid.toUpperCase());
  if (!parentGuid) return null;
  return entityMap.get(parentGuid) ?? null;
}

/** Walk up the parent tree until we reach a geo_country, returning its code. */
function findCountryCode(
  entityGuid: string,
  entityType: string | undefined,
  code: string | undefined,
  parentMap: Map<string, string>,
  entityMap: Map<string, Record<string, any>>,
): string | null {
  // If this entity IS a country, return its code directly
  if ((entityType || "").toLowerCase() === "geo_country" && code) {
    return code.toUpperCase().slice(0, 2);
  }

  // If this entity has a code with a country prefix (e.g. "US-MA"), use it
  if (code) {
    const upper = code.toUpperCase().trim();
    const dash = upper.indexOf("-");
    if (dash === 2) return upper.slice(0, 2);
    if (upper.length === 2 && /^[A-Z]{2}$/.test(upper)) return upper;
  }

  // Walk up the tree (max 5 hops to prevent infinite loops)
  let guid = entityGuid.toUpperCase();
  for (let i = 0; i < 5; i++) {
    const parentGuid = parentMap.get(guid);
    if (!parentGuid) return null;
    const parent = entityMap.get(parentGuid);
    if (!parent) return null;
    const ptype = (parent.entity_type || parent.entityType || "").toLowerCase();
    const pcode = (parent.code || "").toUpperCase().trim();
    if (ptype === "geo_country" && pcode.length === 2) return pcode;
    // Parent has a dash code? Extract country prefix
    if (pcode.indexOf("-") === 2) return pcode.slice(0, 2);
    guid = parentGuid;
  }
  return null;
}

/**
 * Build a display name that includes the next geographic region.
 * e.g. "Boston" → "Boston, Massachusetts" or "Toronto" → "Toronto, Ontario"
 *
 * Strategy:
 * 1. If entity has a code (e.g. "US-AL"), use the static name tables
 * 2. Otherwise, walk the parent-child relationship tree to find the parent name
 */
function qualifiedGeoName(
  name: string,
  entityGuid: string,
  code: string | undefined,
  entityType: string | undefined,
  parentMap: Map<string, string>,
  entityMap: Map<string, Record<string, any>>,
): string {
  if (!entityType) return name;
  const etype = entityType.toLowerCase();

  // For geo_country or geo_planet, just the name is enough
  if (etype === "geo_country" || etype === "geo_planet") return name;

  const upper = (code || "").toUpperCase().trim();
  const dash = upper.indexOf("-");

  // ── Strategy 1: Code-based resolution ──

  if (upper && dash >= 2) {
    const countryCode = upper.slice(0, 2);
    const regionCode = upper.slice(dash + 1);

    // geo_state: "US-AL" → "Alabama, United States"
    if (etype === "geo_state") {
      // Walk up to find the country parent
      const parent = findParent(entityGuid, parentMap, entityMap);
      if (parent) return `${name}, ${parent.name}`;
      return name;
    }

    // geo_city / geo_county / geo_region with a code
    let regionName: string | undefined;
    if (countryCode === "US") regionName = US_STATE_NAMES[regionCode];
    else if (countryCode === "CA") regionName = CA_PROVINCE_NAMES[regionCode];
    else if (countryCode === "IN") regionName = IN_STATE_NAMES[regionCode];
    if (regionName) return `${name}, ${regionName}`;
  }

  // ── Strategy 2: Relationship tree walk ──
  const parent = findParent(entityGuid, parentMap, entityMap);
  if (parent) return `${name}, ${parent.name || ""}`.replace(/, $/, "");

  return name;
}

/* ================================================================
   Chrome
   ================================================================

   DEV-1475 — the ~30 inline style objects that used to live here (the `S`
   const: wrapper, inner, collapsedStrip, header, filters, rows, badges…) are
   GONE. They now live in ActivityDrawer.module.css, on --rail-* tokens and
   logical properties.

   The drawer deliberately does NOT adopt the shared <Rail> component — it is
   glass over the globe and an absolute overlay, not an opaque layout column
   (Skip rulings DEV-1472 / DEV-1475). It follows the Rail chrome CONTRACT
   without the Rail COMPONENT. Full rationale in the module.css header. */

/* ================================================================
   Component
   ================================================================ */

export default function ActivityDrawer({ communities, communityRelationships = [], projects, onHighlight, onOpenChange }: Props) {
  const { t } = useAppTranslation('discover');
  const [filter, setFilter] = useState<FilterMode>("all");

  /* DEV-1475 — collapse now runs on the SAME hook every other rail uses
     (usePersistentCollapse), replacing the bespoke `useState(false)`. Two
     consequences, both wanted:
       - the drawer's open/closed state PERSISTS across sessions, which it never
         did before (you re-opened it on every visit to Discover);
       - `defaultCollapsed: true` keeps Discover's start-collapsed design — the
         globe is the point, the drawer is opt-in.
     `hoveredId` is gone too: row hover is a CSS :hover rule now, so moving the
     mouse down the list no longer re-renders all 25 rows. */
  const { collapsed, setCollapsed } = usePersistentCollapse(
    'l4e_rail_discover_activity_collapsed',
    true,
  );
  const isOpen = !collapsed;

  const toggle = (open: boolean) => {
    setCollapsed(!open);
    onOpenChange?.(open);
  };

  /* The globe's PillarBar reads drawerOpen, and the parent only hears about it
     through onOpenChange — which used to fire only on click. Now that the state
     can arrive from localStorage on mount, announce it once so an
     already-open drawer doesn't leave the globe thinking it's closed. */
  React.useEffect(() => {
    onOpenChange?.(isOpen);
    // Mount-only: subsequent changes are announced by toggle().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Build unified activity feed — last 25 items by creation time (descending). */
  const feed = useMemo<ActivityEntry[]>(() => {
    const entries: ActivityEntry[] = [];
    const epoch = new Date(0); // fallback for entries without timestamps

    // Build lookup maps for relationship-based name/flag resolution
    const entityMap = buildEntityMap(communities);
    const parentMap = buildParentMap(communityRelationships);

    // Community entries (active ones)
    for (const c of communities) {
      const isActive = c.is_active !== false && c.isActive !== false;
      if (!isActive) continue;
      const ts = parseDate(c.created_at || c.createdAt) || epoch;
      const rawName = c.name || "(unnamed)";
      const etype = c.entity_type || c.entityType;
      const code = c.code;
      const guid = c.entity_guid || c.entityGuid || c.id || "";
      const isGeo = (etype || "").toLowerCase().startsWith("geo_");
      entries.push({
        id: `c-${guid}`,
        type: "community",
        name: isGeo ? qualifiedGeoName(rawName, guid, code, etype, parentMap, entityMap) : rawName,
        timestamp: ts,
        displayTime: ts === epoch ? "recently" : timeAgo(ts),
        subtype: etype,
        countryCode: isGeo ? findCountryCode(guid, etype, code, parentMap, entityMap) ?? undefined : undefined,
      });
    }

    // Project entries
    for (const p of projects) {
      const ts = parseDate(p.created_at || p.createdAt) || epoch;
      entries.push({
        id: `p-${p.project_GUID || p.id}`,
        type: "project",
        name: p.project_name || p.name || "(unnamed)",
        timestamp: ts,
        displayTime: ts === epoch ? "recently" : timeAgo(ts),
        subtype: p.project_category || p.projectCategory,
      });
    }

    // Sort newest first, cap at 25
    entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    return entries.slice(0, 25);
  }, [communities, communityRelationships, projects]);

  const filtered = useMemo(() => {
    if (filter === "all") return feed;
    return feed.filter((e) => e.type === filter);
  }, [feed, filter]);

  const handleClick = (entry: ActivityEntry) => {
    if (!onHighlight) return;
    if (entry.type === "community") {
      const guid = entry.id.replace(/^c-/, "");
      const entity = communities.find(
        (c) => normalizeGuid(c.entity_guid || c.entityGuid || c.id) === normalizeGuid(guid)
      );
      if (entity) {
        const coords = resolveEntityCoords(entity, communities, communityRelationships);
        const entityId = entity.entity_guid || entity.entityGuid || entity.id || "";
        if (coords) onHighlight(coords.lat, coords.lon, entityId);
      }
    } else if (entry.type === "project") {
      const guid = entry.id.replace(/^p-/, "");
      const project = projects.find(
        (p) => (p.project_GUID || p.id) === guid
      );
      if (project) {
        const lat = parseFloat(project.location_lat || project.locationLat || "");
        const lon = parseFloat(project.location_lon || project.locationLon || "");
        if (!isNaN(lat) && !isNaN(lon)) onHighlight(lat, lon, project.project_GUID || project.id);
      }
    }
  };

  return (
    <div
      className={`${styles.wrapper} ${isOpen ? styles.wrapperOpen : styles.wrapperClosed}`}
    >
      <div className={styles.inner}>
        {/* ── Collapsed: vertical strip ── */}
        {!isOpen && (
          <button
            type="button"
            className={styles.collapsedStrip}
            onClick={() => toggle(true)}
            aria-expanded={false}
            aria-label={t('activity.header')}
          >
            <span className={styles.collapsedDot} aria-hidden="true" />
            <span className={styles.collapsedLabel}>{t('activity.label')}</span>
            <span className={styles.collapsedChevron} aria-hidden="true">&#9654;</span>
          </button>
        )}

        {/* ── Expanded panel ── */}
        {isOpen && (
          <>
            <div className={styles.header}>
              <span className={styles.headerTitle}>{t('activity.header')}</span>
              {/* Hover is a :hover rule now — the two inline handlers that wrote
                  style.background straight onto the DOM node are gone. */}
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => toggle(false)}
                aria-expanded
                aria-label={t('activity.header')}
              >
                &#9664;
              </button>
            </div>

            <div className={styles.filters}>
              {(["all", "community", "project"] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`${styles.filterBtn} ${filter === f ? styles.filterBtnOn : ""}`}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f === "all"
                    ? t('activity.filterAll')
                    : f === "community"
                      ? t('activity.filterCommunities')
                      : t('activity.filterProjects')}
                </button>
              ))}
            </div>

            {/* Entry list */}
            <div className={styles.list}>
              {filtered.length === 0 && (
                <div className={styles.empty}>
                  {filter === "all"
                    ? t('activity.empty')
                    : t('activity.emptyFiltered', { filter: filter === "community" ? t('activity.filterCommunities') : t('activity.filterProjects') })}
                </div>
              )}
              {filtered.map((entry) => {
                const isProject = entry.type === "project";
                const flag = entry.countryCode ? countryCodeToFlag(entry.countryCode) : null;
                return (
                  <button
                    type="button"
                    key={entry.id}
                    className={`${styles.row} ${isProject ? styles.rowProject : ""}`}
                    onClick={() => handleClick(entry)}
                  >
                    {flag ? (
                      <span className={styles.flagIcon} aria-hidden="true">{flag}</span>
                    ) : (
                      <span
                        className={`${styles.rowIcon} ${isProject ? styles.rowIconProject : ""}`}
                        aria-hidden="true"
                      >
                        {isProject ? "📋" : "🌐"}
                      </span>
                    )}
                    <span className={styles.rowContent}>
                      <span className={styles.rowName}>{entry.name}</span>
                      <span className={styles.rowMeta}>
                        <span className={`${styles.badge} ${isProject ? styles.badgeProject : ""}`}>
                          {isProject
                            ? entry.subtype || "project"
                            : prettyType(entry.subtype || "community")}
                        </span>
                        <span className={styles.rowTime}>{entry.displayTime}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
